import { isExcludedFromCpdPackage } from "@/lib/cpd-package";
import {
  calculateOnlineMultiBuyDiscount,
  type OnlineMultiBuyDiscountTier,
} from "@/lib/online-course-multibuy";
import { basketLineTotalPence, courseSlugFromBasketUrl } from "@/lib/online-basket-checkout";
import { isOnlineBogofEligibleSlugClient } from "@/lib/online-course-bogof-eligible-slugs";
import {
  courseUsesManagedOptions,
  isOnlineHubCategory,
  type LocalOnlineCourseListItem,
} from "@/lib/online-courses-shared";

/** Campaign ends Sunday 30 November 2026 23:59:59 Europe/London. */
export const ONLINE_BOGOF_CAMPAIGN_END_MS = Date.parse(
  "2026-11-30T23:59:59+00:00",
);

/** Buy 3 eligible online courses, pay for 2 - lowest priced course free. */
export const ONLINE_THREE_FOR_TWO_GROUP_SIZE = 3;

export const ONLINE_BOGOF_BANNER_HEADLINE =
  "3 Online Courses for the Price of 2";

export const ONLINE_BOGOF_BANNER_SUBHEADLINE =
  "Add 3 eligible online courses and your lowest-priced course is free. Selected online courses only.";

export const ONLINE_BOGOF_DISCOUNT_LABEL =
  "3 for 2 - Online Courses";

export const ONLINE_BOGOF_BADGE_LABEL = "3 for 2 eligible";

export const ONLINE_BOGOF_TERMS =
  "Offer valid on selected online courses only. Buy 3 eligible online courses in one checkout and your lowest-priced eligible course is free. For every additional 3 eligible courses, another lowest-priced course is free. Excludes onsite VTCT and ITEC qualifications, in-person practical training, kits, case-study upgrades and previously discounted products. Cannot be used with other offers or discount codes. Discount applies automatically at checkout.";

export type OnlineBogofEligibleCourse = Pick<
  LocalOnlineCourseListItem,
  | "active"
  | "qualification_type"
  | "course_type"
  | "purchase_flow"
  | "hub_category"
  | "price_id"
  | "options_managed"
  | "title"
  | "url"
  | "slug"
>;

export type OnlineCheckoutDiscountResult =
  | {
      kind: "bogof";
      discountPence: number;
      freeCount: number;
      eligibleCount: number;
      label: string;
      finalTotalPence: number;
    }
  | {
      kind: "multibuy";
      discountPence: number;
      freeCount: 0;
      eligibleCount: number;
      label: string;
      finalTotalPence: number;
      tier: OnlineMultiBuyDiscountTier | null;
    }
  | {
      kind: "none";
      discountPence: 0;
      freeCount: 0;
      eligibleCount: number;
      label: null;
      finalTotalPence: number;
      tier: null;
    };

export function isOnlineBogofCampaignActive(now = new Date()): boolean {
  return now.getTime() < ONLINE_BOGOF_CAMPAIGN_END_MS;
}

/**
 * Sticky nav 3-for-2 banner: hub + online course detail pages only
 * (not sitewide, and not category/pathway listing pages).
 */
export function shouldShowOnlineBogofNavBanner(
  asPath: string,
  now = new Date(),
): boolean {
  if (!isOnlineBogofCampaignActive(now)) {
    return false;
  }

  const path = asPath.split("?")[0].split("#")[0];
  const normalized = path.endsWith("/") ? path : `${path}/`;

  if (normalized === "/courses/elearning/") {
    return true;
  }

  if (!normalized.startsWith("/courses/elearning/")) {
    return false;
  }

  if (normalized.startsWith("/courses/elearning/pathways/")) {
    return false;
  }

  const match = normalized.match(/^\/courses\/elearning\/([^/]+)\/$/);
  if (!match) {
    return false;
  }

  // Category listing pages (beauty, aesthetics, bundles, etc.)
  if (isOnlineHubCategory(match[1])) {
    return false;
  }

  return true;
}

export function isOnlineBogofEligibleCourse(
  course: OnlineBogofEligibleCourse,
): boolean {
  if (
    !course.active ||
    course.purchase_flow !== "checkout" ||
    !String(course.url || "").includes("/courses/elearning/") ||
    (!Boolean(course.price_id) && !courseUsesManagedOptions(course)) ||
    isExcludedFromCpdPackage({
      title: course.title,
      url: course.url,
      slug: course.slug,
    })
  ) {
    return false;
  }

  const isCpdSingleCourse =
    course.qualification_type === "cpd" &&
    course.course_type === "single-course";
  const isVtctOnlineTheory =
    course.qualification_type === "vtct" &&
    course.hub_category === "vtct-online-theory" &&
    course.course_type === "single-course";
  const isOnlineBundle =
    course.course_type === "bundle" && course.hub_category === "bundles";

  return isCpdSingleCourse || isVtctOnlineTheory || isOnlineBundle;
}

/** 3 for 2: one free (lowest priced) for every 3 eligible courses. */
export function calculateOnlineBogofDiscount(
  eligibleCoursePricesPence: number[],
) {
  const eligibleCount = eligibleCoursePricesPence.length;
  const freeCount = Math.floor(eligibleCount / ONLINE_THREE_FOR_TWO_GROUP_SIZE);

  if (freeCount === 0) {
    const subtotalPence = eligibleCoursePricesPence.reduce(
      (sum, price) => sum + price,
      0,
    );

    return {
      eligibleCount,
      freeCount: 0,
      discountPence: 0,
      subtotalPence,
      finalTotalPence: subtotalPence,
    };
  }

  const sortedPrices = [...eligibleCoursePricesPence].sort((a, b) => a - b);
  const discountPence = sortedPrices
    .slice(0, freeCount)
    .reduce((sum, price) => sum + price, 0);
  const subtotalPence = eligibleCoursePricesPence.reduce(
    (sum, price) => sum + price,
    0,
  );

  return {
    eligibleCount,
    freeCount,
    discountPence,
    subtotalPence,
    finalTotalPence: Math.max(subtotalPence - discountPence, 0),
  };
}

export function getOnlineBogofBasketMessage(eligibleCount: number): string | null {
  if (!isOnlineBogofCampaignActive()) {
    return null;
  }

  if (eligibleCount <= 0) {
    return null;
  }

  const remainder = eligibleCount % ONLINE_THREE_FOR_TWO_GROUP_SIZE;
  const freeApplied = Math.floor(eligibleCount / ONLINE_THREE_FOR_TWO_GROUP_SIZE);

  if (freeApplied === 0) {
    const needed = ONLINE_THREE_FOR_TWO_GROUP_SIZE - eligibleCount;
    return needed === 1
      ? "Add 1 more eligible online course to unlock 3 for the price of 2 - your lowest-priced course free."
      : `Add ${needed} more eligible online courses to unlock 3 for the price of 2 - your lowest-priced course free.`;
  }

  if (remainder === 0) {
    return freeApplied === 1
      ? "Offer applied: your lowest-priced eligible online course is free."
      : `Offer applied: ${freeApplied} lowest-priced eligible online courses are free.`;
  }

  const needed = ONLINE_THREE_FOR_TWO_GROUP_SIZE - remainder;
  return needed === 1
    ? "Offer applied on this basket. Add 1 more eligible course to unlock another free course."
    : `Offer applied on this basket. Add ${needed} more eligible courses to unlock another free course.`;
}

type BasketCourseLike = {
  fields?: {
    bogofEligible?: boolean;
    url?: string | null;
    optionCode?: string | null;
    pricing?: { unit_amount?: number } | null;
    configurationId?: string | null;
  };
};

function resolveBasketLineBogofEligible(course: BasketCourseLike): boolean {
  const slug = courseSlugFromBasketUrl(course.fields?.url || "");
  if (slug && isOnlineBogofEligibleSlugClient(slug)) {
    return true;
  }

  return Boolean(course.fields?.bogofEligible);
}

export function getEligibleCoursePricePenceFromBasketEntry(
  course: BasketCourseLike,
) {
  if (!resolveBasketLineBogofEligible(course)) {
    return null;
  }

  const coursePricePence = course.fields?.pricing?.unit_amount || 0;
  return coursePricePence > 0 ? coursePricePence : null;
}

export function partitionOnlineBasketForBogof(
  courses: BasketCourseLike[],
  additionalPence = 0,
) {
  const eligibleCoursePricesPence: number[] = [];
  let ineligibleCoursePence = 0;
  let kitPence = 0;

  for (const course of courses) {
    const fields = course.fields;
    if (!fields) {
      continue;
    }

    const lineTotal = basketLineTotalPence({ fields });
    const coursePrice = fields.pricing?.unit_amount || 0;
    const kitLinePence = Math.max(lineTotal - coursePrice, 0);
    kitPence += kitLinePence;

    const eligiblePrice = getEligibleCoursePricePenceFromBasketEntry(course);
    if (eligiblePrice != null) {
      eligibleCoursePricesPence.push(eligiblePrice);
      continue;
    }

    ineligibleCoursePence += coursePrice;
  }

  const subtotalPence =
    eligibleCoursePricesPence.reduce((sum, price) => sum + price, 0) +
    ineligibleCoursePence +
    kitPence +
    additionalPence;

  return {
    eligibleCoursePricesPence,
    ineligibleCoursePence,
    kitPence,
    subtotalPence,
  };
}

export function resolveOnlineCheckoutDiscount({
  eligibleCoursePricesPence,
  courseLineItemCount,
  subtotalPence,
  now = new Date(),
}: {
  eligibleCoursePricesPence: number[];
  courseLineItemCount: number;
  subtotalPence: number;
  now?: Date;
}): OnlineCheckoutDiscountResult {
  if (isOnlineBogofCampaignActive(now)) {
    const bogof = calculateOnlineBogofDiscount(eligibleCoursePricesPence);

    if (bogof.discountPence > 0) {
      return {
        kind: "bogof",
        discountPence: bogof.discountPence,
        freeCount: bogof.freeCount,
        eligibleCount: bogof.eligibleCount,
        label: ONLINE_BOGOF_DISCOUNT_LABEL,
        finalTotalPence: Math.max(subtotalPence - bogof.discountPence, 0),
      };
    }

    return {
      kind: "none",
      discountPence: 0,
      freeCount: 0,
      eligibleCount: bogof.eligibleCount,
      label: null,
      finalTotalPence: subtotalPence,
      tier: null,
    };
  }

  // Percentage multi-buy is paused while we push 3-for-2 volume.
  const multiBuy = calculateOnlineMultiBuyDiscount(
    courseLineItemCount,
    subtotalPence,
  );

  if (multiBuy.discountPence > 0 && multiBuy.tier) {
    return {
      kind: "multibuy",
      discountPence: multiBuy.discountPence,
      freeCount: 0,
      eligibleCount: eligibleCoursePricesPence.length,
      label: multiBuy.tier.label,
      finalTotalPence: multiBuy.finalTotalPence,
      tier: multiBuy.tier,
    };
  }

  return {
    kind: "none",
    discountPence: 0,
    freeCount: 0,
    eligibleCount: eligibleCoursePricesPence.length,
    label: null,
    finalTotalPence: subtotalPence,
    tier: null,
  };
}
